// Main import statements - MUST BE AT TOP
import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { setLanguageFromUser } from './i18n';
import './App.css';

// Import custom components
import { Registration, Login } from './components/auth';
import Dashboard from './components/Dashboard';
import LandingPage from './components/LandingPage';
import IndustryLandingPage from './components/IndustryLandingPage';
import FeldModus from './components/FeldModus';
import { ForkStatusBanner } from './components/debug';

// Import utilities
import { PRODUCTION_URL, EXPECTED_BACKEND } from './utils/api';

// Production Ready - VERSION 29 - Multi-Industry
window.EMERGENCY_FIX_LOADED = true;
window.APP_VERSION = 'v13-production';

// NO BFCache manipulation - let Safari handle it naturally
// Previous attempts to force reload on BFCache restore caused infinite loops

// AUTOMATISCHE UMLEITUNG bei falschem Fork -> zur Produktions-Domain
// NUR aktiv wenn wir auf einer Custom Domain sind (nicht in Preview-Umgebung)
(function autoRedirectOnWrongFork() {
  const currentBackend = process.env.REACT_APP_BACKEND_URL || '';
  const currentHost = window.location.hostname;
  
  // Liste der bekannten falschen/veralteten Hosts
  const wrongForks = [
    'preview-build-2.emergent.host',
    'preview-build-1.emergent.host',
    'stable-office-view.preview.emergentagent.com',
    'babelmix-preview.emergentagent.com',
    'safari-ios-fix-1.preview.emergentagent.com',
    'mobile-bugfix-1.preview.emergentagent.com',
    'mobile-bugfix-2.preview.emergentagent.com',
    'appresilience.preview.emergentagent.com'
  ];
  
  // Prüfe ob der aktuelle Backend auf einen falschen/veralteten Host zeigt
  const isWrongFork = wrongForks.some(wrongFork => currentBackend.includes(wrongFork));
  
  // Prüfe ob wir bereits auf der Produktions-Domain sind (Host = babelmix.com)
  const isOnProductionHost = currentHost.includes('babelmix.com');
  const isCorrectBackend = currentBackend.includes('babelmix.com');
  
  // Prüfe ob wir in Preview-Umgebung oder lokal entwickeln
  const isPreview = currentHost.includes('preview.emergentagent.com') || currentHost.includes('emergent.host');
  const isLocalDev = currentHost.includes('localhost') || currentHost.includes('127.0.0.1');
  
  console.log('🔍 Production Check:', {
    currentBackend,
    currentHost,
    isWrongFork,
    isOnProductionHost,
    isCorrectBackend,
    isPreview,
    isLocalDev
  });
  
  // WICHTIG: Wenn wir auf babelmix.com sind aber der Backend-URL falsch ist,
  // müssen wir die API-URL dynamisch korrigieren!
  if (isOnProductionHost) {
    if (isWrongFork || !isCorrectBackend) {
      console.log('⚠️ Auf Produktions-Domain aber falscher Backend-URL erkannt!');
      // Der Fix in api.js sollte das bereits handhaben
      // Hier nur loggen, nicht umleiten (das würde Probleme verursachen)
    }
    return; // Keine Umleitung nötig - wir sind bereits auf Produktion
  }
  
  // In Preview-Umgebung oder lokal: Keine automatische Umleitung
  if (isPreview || isLocalDev) {
    console.log('📱 Preview/Dev Umgebung erkannt - keine automatische Umleitung');
    return;
  }
  
  // Auf einer anderen Domain (z.B. babelmix.de) mit falschem Fork -> umleiten
  if (isWrongFork) {
    console.log('🔄 Falscher Fork auf Custom Domain - Umleitung zu babelmix.com');
    // Umleitung mit kurzem Delay für Logging
    setTimeout(() => {
      window.location.href = PRODUCTION_URL + window.location.pathname + window.location.search;
    }, 100);
  }
})();

// Fork Guard - stellt sicher dass der Benutzer auf der richtigen URL ist
window.FORK_GUARD = {
  isProduction: window.location.hostname.includes('babelmix.com'),
  currentBackend: process.env.REACT_APP_BACKEND_URL || '',
  productionUrl: PRODUCTION_URL,
  expectedBackend: EXPECTED_BACKEND,
  
  // Prüft ob wir auf einem veralteten Fork sind
  isOnWrongFork: function() {
    // Auf Production ist alles OK (api.js korrigiert die URL dynamisch)
    if (this.isProduction) return false;
    // In Preview/Dev nicht warnen
    if (window.location.hostname.includes('preview.emergentagent.com')) return false;
    if (window.location.hostname.includes('localhost')) return false;
    // Ansonsten prüfen ob Backend korrekt ist
    return !this.currentBackend.includes(this.expectedBackend);
  },
  
  // Manuelle Umleitung zur Produktions-Domain
  redirectToProduction: function() {
    window.location.href = this.productionUrl + window.location.pathname + window.location.search;
  }
};

// NUCLEAR CACHE CLEARING - DISABLED FOR SAFARI DEBUGGING
(async function nuclearCacheClear() {
  const currentBackend = process.env.REACT_APP_BACKEND_URL || '';
  localStorage.setItem('last_known_fork', currentBackend);
  localStorage.setItem('app_version', window.APP_VERSION);
})();

// Helper-Funktion: Korrigiert Datei-URLs für Production
window.fixUploadPath = function(path) {
  if (!path) return path;
  if (path.includes('/api/uploads/')) return path;
  return path.replace('/uploads/', '/api/uploads/');
};

// Main App Component
function App() {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLogin, setShowLogin] = useState(false);
  const [showRegistration, setShowRegistration] = useState(false);
  const [showLanding, setShowLanding] = useState(true);
  const [justRegistered, setJustRegistered] = useState(false);
  const [showFeldModus, setShowFeldModus] = useState(false);

  // Check URL for /baustelle route
  useEffect(() => {
    const path = window.location.pathname;
    if (path === '/baustelle' || path === '/quick') {
      setShowFeldModus(true);
    }
  }, []);

  // Set language when user changes
  useEffect(() => {
    if (currentUser) {
      setLanguageFromUser(currentUser);
    }
  }, [currentUser]);

  useEffect(() => {
    const loadUserFromStorage = () => {
      try {
        const savedUser = localStorage.getItem('currentUser');
        if (savedUser && savedUser !== 'null' && savedUser !== 'undefined') {
          const parsedUser = JSON.parse(savedUser);
          if (parsedUser && parsedUser.id) {
            setCurrentUser(parsedUser);
            setLanguageFromUser(parsedUser); // Set language immediately
            setShowLanding(false);
            setLoading(false);
            return;
          }
        }
      } catch (error) {
        console.error('Error loading user from localStorage:', error);
        localStorage.removeItem('currentUser');
      }
      setLoading(false);
    };

    loadUserFromStorage();
  }, []);

  // Handle browser back button - SIMPLIFIED for Safari compatibility
  useEffect(() => {
    const handlePopState = (event) => {
      const state = event.state;
      if (state?.page === 'landing') {
        setShowLogin(false);
        setShowRegistration(false);
        setShowLanding(true);
      } else if (state?.page === 'login') {
        setShowLanding(false);
        setShowRegistration(false);
        setShowLogin(true);
      } else if (state?.page === 'registration') {
        setShowLanding(false);
        setShowLogin(false);
        setShowRegistration(true);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Android Back Button Handler - Prevent accidental app exit
  useEffect(() => {
    // Only enable for PWA/standalone mode on mobile
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                         window.navigator.standalone === true;
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const isAndroid = /Android/i.test(navigator.userAgent);
    
    if (!isStandalone || !isMobile) return;

    let lastBackPress = 0;
    const DOUBLE_PRESS_DELAY = 2000; // 2 seconds to double-tap for exit
    let exitToastEl = null;

    const showExitToast = () => {
      // Remove existing toast if any
      if (exitToastEl) {
        exitToastEl.remove();
      }
      
      // Create toast element
      exitToastEl = document.createElement('div');
      exitToastEl.id = 'exit-toast';
      exitToastEl.innerHTML = isAndroid 
        ? '⬅️ Nochmal drücken zum Beenden' 
        : '⬅️ Tap again to exit';
      exitToastEl.style.cssText = `
        position: fixed;
        bottom: 80px;
        left: 50%;
        transform: translateX(-50%);
        background: rgba(0, 0, 0, 0.8);
        color: white;
        padding: 12px 20px;
        border-radius: 25px;
        font-size: 14px;
        z-index: 9999;
        animation: fadeIn 0.2s ease-out;
        box-shadow: 0 4px 12px rgba(0,0,0,0.3);
      `;
      document.body.appendChild(exitToastEl);
      
      // Auto-remove after delay
      setTimeout(() => {
        if (exitToastEl && exitToastEl.parentNode) {
          exitToastEl.style.animation = 'fadeOut 0.2s ease-out forwards';
          setTimeout(() => exitToastEl.remove(), 200);
        }
      }, DOUBLE_PRESS_DELAY - 200);
    };

    const handleBackButton = (e) => {
      // If user is logged in and on dashboard, prevent immediate exit
      if (currentUser && !showLanding && !showLogin && !showRegistration) {
        const now = Date.now();
        
        if (now - lastBackPress < DOUBLE_PRESS_DELAY) {
          // Double back press - allow exit (don't prevent default)
          if (exitToastEl) exitToastEl.remove();
          return;
        }
        
        lastBackPress = now;
        
        // Prevent the default back action
        e.preventDefault();
        
        // Show exit toast
        showExitToast();
        
        // Push a dummy state to prevent immediate exit
        window.history.pushState({ preventExit: true }, '');
      }
    };

    // Initialize history state
    if (currentUser && !showLanding) {
      window.history.pushState({ preventExit: true }, '');
    }

    // Add CSS animation keyframes
    const styleEl = document.createElement('style');
    styleEl.textContent = `
      @keyframes fadeIn { from { opacity: 0; transform: translateX(-50%) translateY(10px); } to { opacity: 1; transform: translateX(-50%) translateY(0); } }
      @keyframes fadeOut { from { opacity: 1; } to { opacity: 0; } }
    `;
    document.head.appendChild(styleEl);

    window.addEventListener('popstate', handleBackButton);
    
    return () => {
      window.removeEventListener('popstate', handleBackButton);
      if (exitToastEl) exitToastEl.remove();
      if (styleEl) styleEl.remove();
    };
  }, [currentUser, showLanding, showLogin, showRegistration]);
  
  // Navigation helpers that push history state
  const handleGoToLogin = () => {
    window.history.pushState({ page: 'login' }, '', window.location.href);
    setShowLanding(false);
    setShowRegistration(false);
    setShowLogin(true);
  };
  
  const handleGoToRegistration = () => {
    window.history.pushState({ page: 'registration' }, '', window.location.href);
    setShowLanding(false);
    setShowLogin(false);
    setShowRegistration(true);
  };
  
  const handleGoToLanding = () => {
    window.history.pushState({ page: 'landing' }, '', window.location.href);
    setShowLogin(false);
    setShowRegistration(false);
    setShowLanding(true);
  };

  const handleLogout = () => {
    console.log('Logging out user');
    localStorage.removeItem('currentUser');
    setCurrentUser(null);
    setShowLogin(false);
    setShowRegistration(false);
    setShowLanding(true);
  };

  const handleUserRegistered = (user) => {
    console.log('User registered:', user);
    if (user && user.id) {
      try {
        localStorage.setItem('currentUser', JSON.stringify(user));
        console.log('User saved to localStorage after registration');
        setCurrentUser(user);
        setShowLogin(false);
        setShowRegistration(false);
        setShowLanding(false);
        setJustRegistered(true);
        setTimeout(() => setJustRegistered(false), 10000);
      } catch (error) {
        console.error('Error saving user to localStorage after registration:', error);
      }
    }
  };

  const handleUserLoggedIn = (user) => {
    console.log('User logged in:', user);
    if (user && user.id) {
      try {
        localStorage.setItem('currentUser', JSON.stringify(user));
        console.log('User saved to localStorage after login');
        setCurrentUser(user);
        setShowLogin(false);
        setShowRegistration(false);
        setShowLanding(false);
      } catch (error) {
        console.error('Error saving user to localStorage after login:', error);
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Lade...</p>
        </div>
      </div>
    );
  }

  // Logged-in users go directly to Dashboard
  if (currentUser) {
    console.log('User is logged in, showing Dashboard');
    
    // Show Feld-Modus if active
    if (showFeldModus) {
      return (
        <div className="App">
          <FeldModus 
            currentUser={currentUser} 
            onBack={() => {
              setShowFeldModus(false);
              window.history.pushState({}, '', '/');
            }} 
          />
        </div>
      );
    }
    
    return (
      <BrowserRouter>
        <div className="App">
          <ForkStatusBanner />
          <Routes>
            <Route
              path="/"
              element={
                <Dashboard 
                  currentUser={currentUser} 
                  onLogout={handleLogout} 
                  justRegistered={justRegistered}
                  onOpenFeldModus={() => setShowFeldModus(true)}
                />
              }
            />
            <Route
              path="/baustelle"
              element={
                <FeldModus 
                  currentUser={currentUser} 
                  onBack={() => window.location.href = '/'}
                />
              }
            />
          </Routes>
        </div>
      </BrowserRouter>
    );
  }

  // For non-logged-in users: Show Landing Pages with routing support
  // This allows /bau, /labor, /aerospace, /shipbuilding, /international URLs
  
  // Show login screen
  if (showLogin) {
    console.log('Showing login screen');
    return (
      <Login 
        onUserLoggedIn={handleUserLoggedIn} 
        onBackToLanding={() => {
          setShowLogin(false);
          setShowRegistration(false);
        }}
      />
    );
  }

  // Show registration screen
  if (showRegistration) {
    console.log('Showing registration screen');
    return (
      <Registration 
        onUserRegistered={handleUserRegistered} 
        showLoginOption={handleGoToLogin}
        onBackToLanding={() => {
          setShowLogin(false);
          setShowRegistration(false);
        }}
      />
    );
  }

  // Show Landing Pages with industry-specific routing
  console.log('Showing Landing Page with routing');
  return (
    <BrowserRouter>
      <Routes>
        {/* Industry-specific landing pages */}
        <Route
          path="/bau"
          element={
            <IndustryLandingPage 
              industry="bau"
              onClickToRegister={handleGoToRegistration}
              onClickToLogin={handleGoToLogin}
            />
          }
        />
        <Route
          path="/labor"
          element={
            <IndustryLandingPage 
              industry="labor"
              onClickToRegister={handleGoToRegistration}
              onClickToLogin={handleGoToLogin}
            />
          }
        />
        <Route
          path="/aerospace"
          element={
            <IndustryLandingPage 
              industry="aerospace"
              onClickToRegister={handleGoToRegistration}
              onClickToLogin={handleGoToLogin}
            />
          }
        />
        <Route
          path="/shipbuilding"
          element={
            <IndustryLandingPage 
              industry="shipbuilding"
              onClickToRegister={handleGoToRegistration}
              onClickToLogin={handleGoToLogin}
            />
          }
        />
        <Route
          path="/international"
          element={
            <IndustryLandingPage 
              industry="international"
              onClickToRegister={handleGoToRegistration}
              onClickToLogin={handleGoToLogin}
            />
          }
        />
        <Route
          path="/galabau"
          element={
            <IndustryLandingPage 
              industry="galabau"
              onClickToRegister={handleGoToRegistration}
              onClickToLogin={handleGoToLogin}
            />
          }
        />
        {/* Default landing page */}
        <Route
          path="*"
          element={
            <LandingPage 
              onClickToRegister={handleGoToRegistration}
              onClickToLogin={handleGoToLogin}
            />
          }
        />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
